import clsx from "clsx";
import { BiChevronDown } from "react-icons/bi";
import { type MouseEvent, useEffect, useRef, useState } from "react";

interface Option {
  label: string;
  value?: string;
  displayValue?: string;
}

type LabelType = "active" | "sold" | "de_listed";
interface DropdownProps {
  options: Option[];
  label: LabelType;
  positionY: "top" | "bottom";
  customClassName?: string;
  top?: string;
  selectedOptions: { [key: string]: { displayValue: string } };
  setSelectedOption: (
    label: "active" | "sold" | "de_listed",
    option: { value: string; displayValue: string }
  ) => void;
}
const Dropdown = ({
  options,
  label,
  positionY,
  customClassName,
  top = "top-5",
  selectedOptions,
  setSelectedOption,
}: DropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectedOption = selectedOptions[label] || { displayValue: "Select" };

  const dropdownRef = useRef<HTMLDivElement>(null);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleOptionClick = (selectedOption: {
    value?: string;
    displayValue?: string;
  }) => {
    const displayValue = selectedOption.displayValue || "All";
    setSelectedOption(label, {
      value: selectedOption.value || "",
      displayValue,
    });
    setIsOpen(false);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener(
      "mousedown",
      handleClickOutside as unknown as EventListener
    );
    return () => {
      document.removeEventListener(
        "mousedown",
        handleClickOutside as unknown as EventListener
      );
    };
  }, [isOpen]);

  return (
    <div ref={dropdownRef} className={clsx("relative", customClassName)}>
      <button
        type="button"
        onClick={toggleDropdown}
        className="focus:outline-none flex items-center font-bold justify-between"
      >
        {selectedOption.displayValue || label}
        <span className="">
          <BiChevronDown
            className={clsx("text-xl transition-all", isOpen && "rotate-180")}
          />
        </span>
      </button>
      {isOpen && (
        <div
          className={clsx(
            "absolute z-10 mt-2 w-[250px] overflow-hidden bg-white border rounded-lg shadow-[0_4px_6px_-1px_rgba(0,0,0,0.10),0_2px_4px_-2px_rgba(0,0,0,0.05)]",
            positionY === "top" ? top : "bottom-7"
          )}
        >
          {options.map((option) => (
            <div
              key={option.label}
              onClick={() => handleOptionClick(option)}
              onKeyUp={() => handleOptionClick(option)}
              className="px-4 py-2 cursor-pointer hover:bg-[#F8F8F8] text-[#374151] text-sm font-normal"
            >
              {option.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export { Dropdown };
